import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import LoadingRow from "./LoadingRow";
import CopyableContainer from "./CopyableContainer";
import LeagueTabHeading from "./LeagueTabHeading";
import "./styles/tableStyles.css";
import "./styles/matchupCardStyles.css";

const formatScore = (value) => {
    if (value === null || value === undefined) return "";
    const n = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(n)) return String(value);
    return n.toFixed(2);
};

const numberValue = (value) => {
    const n = typeof value === "number" ? value : Number(value);
    return Number.isFinite(n) ? n : 0;
};

const shortLabel = (value, fallback = "Team") => {
    const label = String(value || fallback).trim();
    return label.split(/\s+/)[0] || fallback;
};

const gameType = (diff) => {
    if (diff < 10) {
        return { className: "close", label: "Close Game" };
    }
    if (diff < 28) {
        return { className: "solid", label: "Solid Win" };
    }
    return { className: "blowout", label: "Blowout" };
};

const hasHistory = (h2h) => Boolean(h2h && Number(h2h.total_meetings) > 0);

const TeamRow = ({ team, owner, score, role, result, maxScore }) => {
    const scoreValue = numberValue(score);
    const barWidth = maxScore > 0 ? Math.max((scoreValue / maxScore) * 100, 8) : 0;
    const primaryLabel = owner || team;
    const secondaryLabel = owner && team ? team : null;

    return (
        <div className={`matchup-team-row ${result === "W" ? "winner" : "loser"}`}>
            <div className={`matchup-result-badge ${result === "W" ? "win" : "loss"}`}>
                {result}
            </div>
            <div className="matchup-team-info">
                <div className="matchup-team-name">{primaryLabel}</div>
                {secondaryLabel ? (
                    <div className="matchup-owner">
                        {secondaryLabel} <span>{role}</span>
                    </div>
                ) : (
                    <div className="matchup-owner">
                        <span>{role}</span>
                    </div>
                )}
            </div>
            <div className="matchup-score-block">
                <span className="matchup-score">{formatScore(score)}</span>
                <div
                    className="matchup-score-bar"
                    style={{ width: `${barWidth}%` }}
                />
            </div>
        </div>
    );
};

const SeriesDrawer = ({ game, homeWon, isOpen }) => {
    const h2h = game.h2h;
    if (!hasHistory(h2h)) return null;

    const homeWins = Number(h2h.home_wins || 0);
    const awayWins = Number(h2h.away_wins || 0);
    const totalMeetings = Number(h2h.total_meetings || 0);
    const winFill = totalMeetings ? (homeWins / totalMeetings) * 100 : 0;
    const homeShort = shortLabel(game.home_owner || game.home_team, "Home");
    const awayShort = shortLabel(game.away_owner || game.away_team, "Away");
    const leader =
        homeWins > awayWins
            ? { className: "", label: homeShort, suffix: "leads all-time" }
            : awayWins > homeWins
              ? { className: "trail", label: awayShort, suffix: "leads all-time" }
              : { className: "", label: "All-time", suffix: "tied" };

    return (
        <div className={`matchup-drawer-body ${isOpen ? "open" : ""}`}>
            {game.is_final === false ? (
                <div className="matchup-live-note">
                    <span className="matchup-live-pulse" />
                    Game in progress - not counted in this record
                </div>
            ) : null}
            <div className="matchup-record-bar">
                <span className="matchup-record-wl">
                    {homeWins}-{awayWins}
                </span>
                <span className={`matchup-record-leader ${leader.className}`}>
                    <b>{leader.label}</b> {leader.suffix}
                </span>
                <div className="matchup-record-avgs">
                    <span className="matchup-record-label">Avg pts</span>
                    <span className="matchup-record-value">
                        {formatScore(h2h.home_avg_pts)} /{" "}
                        {formatScore(h2h.away_avg_pts)}
                    </span>
                </div>
            </div>
            <div className="matchup-win-bar">
                <div
                    className="matchup-win-fill"
                    style={{ width: `${winFill}%` }}
                />
            </div>
            <div className="matchup-game-list">
                {(h2h.recent_meetings || []).map((meeting, idx) => {
                    const homeMeetingWon = meeting.winner === "home";
                    const winnerLabel =
                        meeting.winner === "home"
                            ? homeShort
                            : meeting.winner === "away"
                              ? awayShort
                              : "Tie";
                    return (
                        <div
                            className={`matchup-game-row ${
                                meeting.is_current ? "current" : ""
                            }`}
                            key={`${meeting.year}-${meeting.week}-${idx}`}
                        >
                            <span
                                className={`matchup-game-dot ${
                                    homeMeetingWon ? "home" : "away"
                                }`}
                            />
                            <span className="matchup-game-period">
                                {meeting.year} Wk {meeting.week}
                            </span>
                            <span className="matchup-game-scores">
                                {formatScore(meeting.home_score)} -{" "}
                                {formatScore(meeting.away_score)}
                            </span>
                            <span
                                className={`matchup-game-result ${
                                    homeMeetingWon === homeWon ? "win" : "loss"
                                }`}
                            >
                                {winnerLabel}
                            </span>
                            {meeting.is_current ? (
                                <span className="matchup-game-tag">This wk</span>
                            ) : (
                                <span />
                            )}
                        </div>
                    );
                })}
            </div>
            {Number(h2h.playoff_meetings || 0) > 0 ? (
                <div className="matchup-playoff-note">
                    Playoffs: {h2h.playoff_meetings} meeting
                    {Number(h2h.playoff_meetings) === 1 ? "" : "s"}
                </div>
            ) : null}
        </div>
    );
};

const BoxScoresTable = ({
    data,
    week,
    loading,
    title = "Box Scores",
    fileNamePrefix = "boxscores",
}) => {
    const [openDrawers, setOpenDrawers] = useState({});

    const toggleDrawer = (index) => {
        setOpenDrawers((current) => ({
            ...current,
            [index]: !current[index],
        }));
    };

    return (
        <CopyableContainer
            title={
                <LeagueTabHeading
                    eyebrow={title}
                    title={`Week ${week}`}
                    className="mb-0"
                />
            }
            fileName={`${fileNamePrefix}-week-${week}`}
        >
            {loading || !data ? (
                <table className="matchup-loading-table">
                    <tbody>
                        <LoadingRow text="Loading Box Scores..." colSpan="1" />
                    </tbody>
                </table>
            ) : data.length === 0 ? (
                <div className="matchup-empty-state">No box scores available.</div>
            ) : (
                <div className="matchup-card-grid">
                    {data.map((game, index) => {
                        const homeScore = numberValue(game.home_score);
                        const awayScore = numberValue(game.away_score);
                        const homeWon = homeScore >= awayScore;
                        const diff = Math.abs(homeScore - awayScore);
                        const total = homeScore + awayScore;
                        const maxScore = Math.max(homeScore, awayScore);
                        const type = gameType(diff);
                        const isOpen = Boolean(openDrawers[index]);
                        const history = hasHistory(game.h2h);

                        return (
                            <article className="matchup-card" key={index}>
                                <header className="matchup-card-header">
                                    <span className="matchup-label">
                                        Week {week} - Matchup {index + 1}
                                    </span>
                                    <span className="matchup-header-chips">
                                        <span
                                            className={`matchup-chip ${type.className}`}
                                        >
                                            {type.label}
                                        </span>
                                        <span
                                            className={`matchup-chip ${
                                                game.is_final === false
                                                    ? "live"
                                                    : "final"
                                            }`}
                                        >
                                            {game.is_final === false
                                                ? "Live"
                                                : "Final"}
                                        </span>
                                    </span>
                                </header>
                                <TeamRow
                                    team={game.home_team}
                                    owner={game.home_owner}
                                    score={game.home_score}
                                    role="Home"
                                    result={homeWon ? "W" : "L"}
                                    maxScore={maxScore}
                                />
                                <TeamRow
                                    team={game.away_team}
                                    owner={game.away_owner}
                                    score={game.away_score}
                                    role="Away"
                                    result={homeWon ? "L" : "W"}
                                    maxScore={maxScore}
                                />
                                <footer className="matchup-card-footer">
                                    <span>
                                        Total: <b>{formatScore(total)} pts</b>
                                    </span>
                                    <b className="matchup-margin">
                                        +{formatScore(diff)}
                                    </b>
                                </footer>
                                {history ? (
                                    <>
                                        <button
                                            className={`matchup-drawer-toggle ${
                                                isOpen ? "open" : ""
                                            }`}
                                            type="button"
                                            onClick={() => toggleDrawer(index)}
                                            aria-expanded={isOpen}
                                        >
                                            <span className="matchup-toggle-left">
                                                <span>Series History</span>
                                                <span className="matchup-toggle-count">
                                                    {game.h2h.total_meetings} meetings
                                                    - {game.h2h.home_wins}W-
                                                    {game.h2h.away_wins}L
                                                </span>
                                            </span>
                                            <ChevronDown
                                                className="matchup-toggle-icon"
                                                size={16}
                                                aria-hidden="true"
                                            />
                                        </button>
                                        <SeriesDrawer
                                            game={game}
                                            homeWon={homeWon}
                                            isOpen={isOpen}
                                        />
                                    </>
                                ) : null}
                            </article>
                        );
                    })}
                </div>
            )}
        </CopyableContainer>
    );
};

export default BoxScoresTable;
